// src/pages/WorksManager.jsx  (or wherever your WorksManager file is)
import React, { useEffect, useState, useContext, useMemo } from "react";
import axios from "axios";
import "./styles/SiteManager.css"; // reuse styles
import { AuthContext } from "../context/AuthContext";

export default function WorksManager({ onWorkCreated }) {
  const { user } = useContext(AuthContext);
  const role = user?.role;

  const isAdmin = ["admin", "super-admin", "Manager"].includes(role);
  const canPatchStatus = ["technician", "admin", "super-admin", "Manager"].includes(role);

  const token = localStorage.getItem("token");
  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const [works, setWorks] = useState([]);
  const [form, setForm] = useState({
    name: "",
    description: "",
    location: "",
    lastStatus: "working",
    lastAction: "none",
    lastParts: "", // csv in UI
    lastStatusAt: "",
  });

  const [editingId, setEditingId] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // history + notes + comments (kept as-is)
  const [historyOpenId, setHistoryOpenId] = useState(null);
  const [history, setHistory] = useState([]);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [commentDrafts, setCommentDrafts] = useState({});

  const allowedStatus = ["working", "down", "needs_service", "degraded", "offline"];
  const allowedAction = ["none", "inspect", "repair", "replace"];

  const fetchWorks = async () => {
    try {
      setLoading(true);
      setError("");
      const url = q ? `/api/works?q=${encodeURIComponent(q)}` : "/api/works";
      const res = await axios.get(url, authHeader);
      setWorks(res.data || []);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to fetch works");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setForm({
      name: "",
      description: "",
      location: "",
      lastStatus: "working",
      lastAction: "none",
      lastParts: "",
      lastStatusAt: "",
    });
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;

    try {
      setLoading(true);
      setError("");

      const payload = {
        ...form,
        lastParts:
          typeof form.lastParts === "string"
            ? form.lastParts
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
            : form.lastParts,
      };

      if (editingId) {
        await axios.put(`/api/works/${editingId}`, payload, authHeader);
      } else {
        await axios.post("/api/works", payload, authHeader);
        if (onWorkCreated) onWorkCreated();
      }

      resetForm();
      await fetchWorks();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to save work");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (w) => {
    setEditingId(w._id);
    setForm({
      name: w.name || "",
      description: w.description || "",
      location: w.location || "",
      lastStatus: w.lastStatus || "working",
      lastAction: w.lastAction || "none",
      lastParts: Array.isArray(w.lastParts) ? w.lastParts.join(", ") : w.lastParts || "",
      lastStatusAt: w.lastStatusAt ? new Date(w.lastStatusAt).toISOString().slice(0, 16) : "",
    });
  };

  const handleDelete = async (id) => {
    if (!isAdmin) return;
    if (!window.confirm("Delete this work?")) return;

    try {
      setLoading(true);
      await axios.delete(`/api/works/${id}`, authHeader);
      await fetchWorks();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to delete work");
    } finally {
      setLoading(false);
    }
  };

  const patchStatus = async (id, update) => {
    if (!canPatchStatus) return;
    try {
      setLoading(true);
      await axios.patch(`/api/works/${id}/status`, update, authHeader);
      await fetchWorks();
      if (update.note) setNoteDrafts((prev) => ({ ...prev, [id]: "" }));
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  const addComment = async (id) => {
    const note = (commentDrafts[id] || "").trim();
    if (!note) return;

    try {
      setLoading(true);
      setError("");
      await axios.post(`/api/works/${id}/comment`, { note }, authHeader);
      setCommentDrafts((prev) => ({ ...prev, [id]: "" }));

      if (historyOpenId === id) {
        const { data } = await axios.get(`/api/works/${id}/actions`, authHeader);
        setHistory(data || []);
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to add comment");
    } finally {
      setLoading(false);
    }
  };

  const toggleHistory = async (id) => {
    if (historyOpenId === id) {
      setHistoryOpenId(null);
      setHistory([]);
      return;
    }

    try {
      setLoading(true);
      setError("");
      const { data } = await axios.get(`/api/works/${id}/actions`, authHeader);
      setHistory(data || []);
      setHistoryOpenId(id);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to load history");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="site-manager-container">
      <div className="list-header">
        <h2 className="site-list-title">Existing Works</h2>

        <div className="search-wrap">
          <input
            type="text"
            placeholder="Search by name..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button onClick={fetchWorks} disabled={loading}>
            Search
          </button>
          <button
            onClick={() => {
              setQ("");
              fetchWorks();
            }}
            disabled={loading}
          >
            Reset
          </button>
        </div>
      </div>

      {error && <div className="error-box">{error}</div>}

      {/* ✅ FIXED TABLE WRAPPER (scroll + sticky header + correct alignment) */}
      <div className="table-card">
        <div className="table-scroll">
          <table className="site-list">
            <thead>
              <tr>
                <th className="col-name">Name</th>
                <th className="col-loc">Location</th>
                <th className="col-desc">Description</th>
                <th className="col-status">Last Status</th>
                <th className="col-action">Last Action</th>
                <th className="col-parts">Last Parts</th>
                <th className="col-at">Last Status At</th>
                <th className="col-actions">Actions</th>
              </tr>
            </thead>

            <tbody>
              {works.map((w) => (
                <React.Fragment key={w._id}>
                  <tr>
                    <td className="col-name">
                      <span className="cell-wrap">{w.name}</span>
                    </td>
                    <td className="col-desc">
  <span className="cell-wrap">{w.location || "-"}</span>
                    </td>
                    <td className="col-desc">
                      <span className="cell-wrap">{w.description || "-"}</span>
                    </td>
                    <td className="col-status"><span className="cell-wrap">{w.lastStatus || "-"}</span></td>
                    <td className="col-action"><span className="cell-wrap">{w.lastAction || "-"}</span></td>
                    <td className="col-parts truncate"><span className="cell-wrap">
                      {Array.isArray(w.lastParts) ? w.lastParts.join(", ") : w.lastParts || "-"}
                    </span></td>
                    <td className="col-at"><span className="cell-wrap">
                      {w.lastStatusAt ? new Date(w.lastStatusAt).toLocaleString() : "-"}
                    </span></td>

                    <td className="col-actions">
                      <div className="action-buttons">
                        {isAdmin && (
                          <>
                            <button className="edit-btn" onClick={() => handleEdit(w)}>
                              Edit
                            </button>
                            <button className="delete-btn" onClick={() => handleDelete(w._id)}>
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* history section stays same */}
                  {historyOpenId === w._id && (
                    <tr className="history-row">
                      <td colSpan={8}>
                        {history.length === 0 ? (
                          <div className="empty">No actions yet.</div>
                        ) : (
                          <ul className="timeline">
                            {history.map((h) => (
                              <li key={h._id}>
                                <div className="time">
                                  {new Date(h.at || h.createdAt).toLocaleString()}
                                </div>
                                <div className="meta">
                                  <strong>{h.action}</strong> by{" "}
                                  <em>{h.byUsername || h.byUser}</em>
                                </div>
                                {h.note && <div className="note">{h.note}</div>}
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* FORM */}
      <div className="site-form">
        {isAdmin && (
          <>
            <h2>{editingId ? "Edit Work" : "Add Work"}</h2>
            <form onSubmit={handleSubmit}>
              <input
                type="text"
                placeholder="Work Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
              <input
                type="text"
                placeholder="Location"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
              />

              <textarea
                placeholder="Description"
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />

              <div className="row-2-cols">
                <div>
                  <label>Last Status</label>
                  <select
                    value={form.lastStatus}
                    onChange={(e) => setForm({ ...form, lastStatus: e.target.value })}
                  >
                    {allowedStatus.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label>Last Action</label>
                  <select
                    value={form.lastAction}
                    onChange={(e) => setForm({ ...form, lastAction: e.target.value })}
                  >
                    {allowedAction.map((a) => (
                      <option key={a} value={a}>
                        {a}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <input
                type="text"
                placeholder="Last Parts (comma separated)"
                value={form.lastParts}
                onChange={(e) => setForm({ ...form, lastParts: e.target.value })}
              />

              <label>Last Status At</label>
              <input
                type="datetime-local"
                value={form.lastStatusAt}
                onChange={(e) => setForm({ ...form, lastStatusAt: e.target.value })}
              />

              <button type="submit" disabled={loading}>
                {editingId ? "Update" : "Add"}
              </button>

              {editingId && (
                <button type="button" onClick={resetForm} className="secondary-btn" disabled={loading}>
                  Cancel
                </button>
              )}
            </form>
          </>
        )}
      </div>

      {loading && <div className="loading">Loading...</div>}
    </div >
  );
}